import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlane,
  FaGraduationCap,
  FaUsers,
  FaGlobeEurope,
  FaBriefcase,
  FaHeart,
  FaCheckCircle,
  FaFileAlt,
  FaClock,
  FaHeadset,
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const ServicesPage = () => {
  const allServices = [
    {
      icon: <FaPlane className="text-4xl" />,
      title: 'Tourist Visas',
      description: 'Explore destinations worldwide with our comprehensive tourist visa services.',
      features: [
        'Single & Multiple Entry Visas',
        'Express Processing Available',
        'Travel Insurance Assistance',
        'Itinerary & Hotel Booking Support',
        'Visa Extension Services'
      ],
      processingTime: '3-15 Business Days',
      successRate: '98%',
      color: 'from-blue-500 to-blue-700',
      path: '/services/tourist'
    },
    {
      icon: <FaGraduationCap className="text-4xl" />,
      title: 'Student Visas',
      description: 'Achieve your academic dreams with our student visa expertise.',
      features: [
        'University Admission Support',
        'Financial Documentation',
        'Statement of Purpose Review',
        'Post-Study Work Visa Guidance',
        'Dependent Visa Processing'
      ],
      processingTime: '4-8 Weeks',
      successRate: '96%',
      color: 'from-green-500 to-green-700',
      path: '/services/student'
    },
    {
      icon: <FaGlobeEurope className="text-4xl" />,
      title: 'Schengen Visas',
      description: 'Travel across 26 European countries with a single visa.',
      features: [
        'Multiple Country Itineraries',
        'Travel Medical Insurance',
        'Hotel & Flight Bookings',
        'Business Invitation Letters',
        'Visa Type Selection Guidance'
      ],
      processingTime: '10-20 Business Days',
      successRate: '95%',
      color: 'from-purple-500 to-purple-700',
      path: '/services/schengen'
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: 'Family Visas',
      description: 'Reunite with your loved ones across borders.',
      features: [
        'Spouse & Partner Visas',
        'Parent & Child Visas',
        'Document Verification',
        'Relationship Evidence Preparation',
        'Permanent Residence Pathway'
      ],
      processingTime: '3-12 Months',
      successRate: '92%',
      color: 'from-pink-500 to-pink-700',
      path: '/services/family'
    },
    {
      icon: <FaBriefcase className="text-4xl" />,
      title: 'Business Visas',
      description: 'Expand your business globally with our visa support.',
      features: [
        'Business Invitation Letters',
        'Conference & Meeting Visas',
        'Investment Visas',
        'Corporate Documentation',
        'Multiple Entry Long-Term Visas'
      ],
      processingTime: '5-15 Business Days',
      successRate: '97%',
      color: 'from-orange-500 to-orange-700',
      path: '/services/business'
    },
    {
      icon: <FaHeart className="text-4xl" />,
      title: 'Medical Visas',
      description: 'Access world-class healthcare facilities abroad.',
      features: [
        'Medical Appointment Letters',
        'Treatment Cost Estimates',
        'Medical History Documentation',
        'Attendant Visa Processing',
        'Follow-up Treatment Visas'
      ],
      processingTime: '7-14 Business Days',
      successRate: '94%',
      color: 'from-red-500 to-red-700',
      path: '/services/medical'
    },
  ];

  const whyChooseUs = [
    {
      icon: <FaCheckCircle className="text-2xl" />,
      title: '95% Success Rate',
      description: 'Highest visa approval rate in the industry'
    },
    {
      icon: <FaFileAlt className="text-2xl" />,
      title: 'Document Review',
      description: 'Thorough verification of all documents'
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: 'Fast Processing',
      description: 'Express services available'
    },
    {
      icon: <FaHeadset className="text-2xl" />,
      title: '24/7 Support',
      description: 'Dedicated consultant for each client'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-900 to-primary-700 text-white py-20">
        <div className="container-padding text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold mb-6"
          >
            Our Visa Services
          </motion.h1>
          <p className="text-xl max-w-3xl mx-auto text-primary-100">
            Comprehensive visa solutions tailored to your specific needs. 
            From tourist visits to permanent residency, we've got you covered.
          </p>
        </div>
      </section>

      {/* All Services */}
      <section className="py-20">
        <div className="container-padding">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${service.color} text-white`}>
                      {service.icon}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Processing</div>
                      <div className="font-bold text-gray-900">{service.processingTime}</div>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center text-gray-700">
                        <FaCheckCircle className="mr-3 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-500">Success Rate</div>
                      <div className="text-2xl font-bold text-green-600">
                        {service.successRate}
                      </div>
                    </div>
                    <Link
                      to={service.path}
                      className="btn-primary"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose GlobalVisa?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine expertise with personalized service for your visa success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-primary-700 text-white">
        <div className="container-padding text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Start Your Visa Journey?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
            Get a free consultation with our visa experts and discover the best 
            pathway for your travel or immigration goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-primary bg-white text-primary-700 hover:bg-primary-100"
            >
              Book Free Consultation
            </Link>
            <Link
              to="/contact"
              className="btn-secondary border-white text-white hover:bg-white/10"
            >
              Call Now: 0313-5993810
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;